import { useState } from 'react';
import {
  AlertCircle,
  Boxes,
  Download,
  PackagePlus,
  Pencil,
  RotateCcw,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';

import { ImportDialog } from '@/components/catalog/import-dialog';
import { ProductFormDrawer } from '@/components/catalog/product-form';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorAlert,
  PageHeader,
  Select,
  Skeleton,
  Table,
  Td,
  Th,
} from '@/components/ui';
import { downloadFile } from '@/lib/api-client';
import {
  useBrands,
  useCategories,
  useDeleteProduct,
  useProducts,
  useRestoreProduct,
  toCategoryTree,
  type ProductListParams,
  type ProductStatus,
} from '@/lib/catalog-api';
import { statusTone } from '@/lib/status';
import { errorMessage, formatCurrency, formatNumber } from '@/lib/utils';

const STATUS_TABS: Array<{ label: string; value: ProductStatus[] }> = [
  { label: 'All', value: [] },
  { label: 'Live', value: ['ACTIVE'] },
  { label: 'Drafts', value: ['DRAFT'] },
  { label: 'Inactive', value: ['INACTIVE'] },
  { label: 'Archived', value: ['ARCHIVED'] },
];

/**
 * Product catalogue.
 *
 * Reads the **admin** catalogue API rather than the public one, so drafts and
 * archived products are visible — a merchandiser's first question is usually
 * "where is the draft I started", and the storefront endpoint would hide it.
 */
export function ProductsPage() {
  const [params, setParams] = useState<ProductListParams>({ page: 1, limit: 20 });
  const [tab, setTab] = useState(0);
  const [editing, setEditing] = useState<{ id: string | null } | null>(null);
  const [importing, setImporting] = useState(false);

  const products = useProducts(params);
  const categories = useCategories();
  const brands = useBrands();
  const remove = useDeleteProduct();
  const restore = useRestoreProduct();

  /** Any filter change returns to page 1; staying on page 7 of a new filter is never right. */
  const patch = (changes: Partial<ProductListParams>): void =>
    setParams((previous) => ({ ...previous, ...changes, page: 1 }));

  const data = products.data;

  return (
    <>
      <PageHeader
        title="Products"
        description="Manage your wholesale catalogue, pricing tiers and stock"
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setImporting(true)}>
              <Upload className="size-4" aria-hidden="true" />
              Import CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                void downloadFile('/admin/catalog/products/export', 'products.csv', {
                  query: {
                    search: params.search || undefined,
                    status: params.status?.length ? params.status.join(',') : undefined,
                    categoryId: params.categoryId || undefined,
                    brandId: params.brandId || undefined,
                  },
                })
              }
            >
              <Download className="size-4" aria-hidden="true" />
              Export
            </Button>
            <Button size="sm" onClick={() => setEditing({ id: null })}>
              <PackagePlus className="size-4" aria-hidden="true" />
              Add Product
            </Button>
          </div>
        }
      />

      {(remove.isError || restore.isError) && (
        <div className="mb-4">
          <ErrorAlert>{errorMessage(remove.error ?? restore.error)}</ErrorAlert>
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-4">
          <div className="relative min-w-56 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            />
            <input
              type="search"
              value={params.search ?? ''}
              onChange={(event) => patch({ search: event.target.value })}
              placeholder="Search by name, SKU, material..."
              aria-label="Search products"
              className="w-full rounded-lg border border-line py-2 pl-9 pr-3 text-sm placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none"
            />
          </div>

          <Select
            aria-label="Filter by category"
            className="w-auto"
            value={params.categoryId ?? ''}
            onChange={(event) => patch({ categoryId: event.target.value || undefined })}
          >
            <option value="">All categories</option>
            {toCategoryTree(categories.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {'  '.repeat(category.depth)}
                {category.name}
              </option>
            ))}
          </Select>

          <Select
            aria-label="Filter by brand"
            className="w-auto"
            value={params.brandId ?? ''}
            onChange={(event) => patch({ brandId: event.target.value || undefined })}
          >
            <option value="">All brands</option>
            {(brands.data ?? []).map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </Select>

          <Checkbox
            label="Low stock"
            checked={params.lowStock ?? false}
            onChange={(event) => patch({ lowStock: event.target.checked || undefined })}
          />

          {data && (
            <span className="ml-auto text-sm text-ink-subtle">
              {formatNumber(data.pagination.total)} products
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-1 border-b border-line px-4 py-2">
          {STATUS_TABS.map((entry, index) => (
            <button
              key={entry.label}
              type="button"
              onClick={() => {
                setTab(index);
                patch({
                  status: entry.value.length ? entry.value : undefined,
                  // Archived products are soft-deleted, so they only appear when
                  // deleted rows are explicitly included.
                  includeDeleted: entry.value.includes('ARCHIVED') || undefined,
                });
              }}
              className={
                tab === index
                  ? 'rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-medium text-brand-700'
                  : 'rounded-lg px-3 py-1.5 text-xs font-medium text-ink-muted hover:bg-canvas'
              }
            >
              {entry.label}
            </button>
          ))}
        </div>

        {products.isPending && (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-14 w-full" />
            ))}
          </div>
        )}

        {products.isError && (
          <div className="p-4">
            <div className="flex items-start gap-3 rounded-lg bg-danger-50 p-4 text-sm text-danger-700">
              <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <div>
                <p className="font-medium">Could not load products.</p>
                <p className="mt-0.5">
                  {errorMessage(products.error, 'Is the API running on port 4000?')}
                </p>
              </div>
            </div>
          </div>
        )}

        {data && data.items.length === 0 && (
          <div className="p-4">
            <EmptyState
              title="No products found"
              description="Try a different search or filter, or add your first product."
            />
          </div>
        )}

        {data && data.items.length > 0 && (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Product</Th>
                  <Th>Status</Th>
                  <Th>Category</Th>
                  <Th className="text-right">MOQ</Th>
                  <Th className="text-right">Price</Th>
                  <Th>Tiers</Th>
                  <Th className="text-right">Stock</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((product) => (
                  <tr key={product.id} className="hover:bg-canvas">
                    <Td>
                      <div className="flex items-center gap-2.5">
                        <span className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-lg bg-canvas">
                          <Boxes className="size-4 text-ink-subtle" aria-hidden="true" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-ink">{product.name}</p>
                          <p className="text-xs text-ink-subtle">
                            {product.sku}
                            {product.material && ` · ${product.material}`}
                          </p>
                        </div>
                      </div>
                    </Td>

                    <Td>
                      <div className="flex flex-wrap gap-1">
                        <Badge tone={statusTone(product.status)}>{product.status}</Badge>
                        {product.deletedAt && <Badge tone="danger">Deleted</Badge>}
                      </div>
                    </Td>

                    <Td className="text-ink-muted">{product.category?.name ?? '—'}</Td>
                    <Td className="text-right font-medium text-ink">{formatNumber(product.moq)}</Td>

                    <Td className="text-right">
                      <p className="font-semibold text-ink">
                        {formatCurrency(product.wholesalePrice)}
                      </p>
                      {product.discountPercent > 0 && (
                        <p className="text-xs text-success-600">{product.discountPercent}% off</p>
                      )}
                    </Td>

                    <Td>
                      {product.tierCount > 0 ? (
                        <span className="text-xs text-ink-muted">
                          {product.tierCount} tiers · from{' '}
                          {formatCurrency(product.lowestTierPrice ?? product.wholesalePrice)}
                        </span>
                      ) : (
                        <span className="text-xs text-ink-subtle">—</span>
                      )}
                    </Td>

                    <Td className="text-right">
                      {product.isUntracked ? (
                        <span className="text-xs text-ink-subtle">Not tracked</span>
                      ) : (
                        <>
                          <span
                            className={
                              product.isLowStock
                                ? 'font-medium text-warning-700'
                                : 'font-medium text-ink'
                            }
                          >
                            {formatNumber(product.availableQuantity)}
                          </span>
                          {product.reservedQuantity > 0 && (
                            <p className="text-xs text-ink-subtle">
                              {formatNumber(product.reservedQuantity)} reserved
                            </p>
                          )}
                        </>
                      )}
                    </Td>

                    <Td>
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setEditing({ id: product.id })}
                          aria-label={`Edit ${product.name}`}
                          className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-canvas hover:text-brand-600"
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </button>

                        {product.deletedAt ? (
                          <button
                            type="button"
                            onClick={() => restore.mutate(product.id)}
                            aria-label={`Restore ${product.name}`}
                            className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-canvas hover:text-success-600"
                          >
                            <RotateCcw className="size-4" aria-hidden="true" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Archive "${product.name}"? It disappears from the storefront but stays on past orders.`,
                                )
                              ) {
                                remove.mutate(product.id);
                              }
                            }}
                            aria-label={`Archive ${product.name}`}
                            className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-danger-50 hover:text-danger-600"
                          >
                            <Trash2 className="size-4" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <div className="flex items-center justify-between border-t border-line px-4 py-3">
              <span className="text-sm text-ink-subtle">
                Page {data.pagination.page} of {data.pagination.totalPages}
                {params.lowStock && ' · showing only low-stock rows from this page'}
              </span>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!data.pagination.hasPreviousPage}
                  onClick={() => setParams((p) => ({ ...p, page: Math.max(1, (p.page ?? 1) - 1) }))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!data.pagination.hasNextPage}
                  onClick={() => setParams((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      <ProductFormDrawer
        open={editing !== null}
        productId={editing?.id ?? null}
        onClose={() => setEditing(null)}
      />

      <ImportDialog open={importing} onClose={() => setImporting(false)} />
    </>
  );
}
